/**
 * Catalog service — lazy, read-only access to all static celestial data.
 *
 * All data is generated at build time into data/*.ts (offline, zero network
 * at runtime). This module builds:
 *   - star records (packed Float32Array + string arrays)
 *   - deep-sky objects
 *   - constellation metadata and line segments
 *   - an in-memory search index (names + designations + aliases)
 *
 * Dynamic solar-system bodies (Sun/Moon/planets) are searchable through the
 * same index; their positions are resolved per-frame by the ephemeris
 * (dynamicObjects.ts), never stored here.
 *
 * The search index is built once, on first use, and is a plain Map<string,
 * string[]> mapping normalized search terms to object IDs.
 */

import { bodyId, bodySearchTerms, describeDynamicObject, DYNAMIC_BODIES } from "./dynamicObjects";
import { DEEP_SKY, type DeepSkyObject } from "./data/deepSky";
import {
  CONSTELLATIONS,
  CONSTELLATION_FALLBACK_POINTS,
  CONSTELLATION_LINES,
  type ConstellationMeta,
} from "./data/constellations";
import {
  STAR_COUNT,
  starConstellations,
  starData,
  starDesignations,
  starHip,
  starNames,
} from "./data/stars";
import type { CelestialObject } from "./objectModel";

const BYTE_OFFSETS = 4; // ra, dec, mag, ci

export interface StarRecord {
  index: number;
  hip: number | null;
  name: string;
  designation: string;
  constellation: string;
  raHours: number;
  decDegrees: number;
  magnitude: number;
  /** B-V color index, or null when unknown. */
  colorIndex: number | null;
}

/** Lazy singleton catalog. */
class StarCatalog {
  get count(): number {
    return STAR_COUNT;
  }

  star(index: number): StarRecord {
    if (!Number.isInteger(index) || index < 0 || index >= STAR_COUNT) {
      throw new RangeError(`star index out of range: ${index} (count ${STAR_COUNT})`);
    }
    const o = index * BYTE_OFFSETS;
    const ci = starData[o + 3]!;
    return {
      index,
      hip: starHip[index]! >= 0 ? starHip[index]! : null,
      name: starNames[index] ?? "",
      designation: starDesignations[index] ?? "",
      constellation: starConstellations[index] ?? "",
      raHours: starData[o]! / 15,
      decDegrees: starData[o + 1]!,
      magnitude: starData[o + 2]!,
      colorIndex: ci >= -8.5 ? ci : null,
    };
  }

  /** Iterate a magnitude-limited subset (useful for rendering). */
  *brightStars(limitMagnitude: number, step = 1): Generator<StarRecord> {
    for (let i = 0; i < STAR_COUNT; i += step) {
      const mag = starData[i * BYTE_OFFSETS + 2]!;
      if (mag <= limitMagnitude) yield this.star(i);
    }
  }
}

export const starCatalog = new StarCatalog();

/** All deep-sky objects, as catalog records (indices match DEEP_SKY). */
export const deepSkyObjects: ReadonlyArray<DeepSkyObject & { index: number }> = DEEP_SKY.map((dso, index) => ({
  ...dso,
  index,
}));

const CONSTELLATION_META_BY_ID: ReadonlyMap<string, ConstellationMeta> = new Map(
  CONSTELLATIONS.map((c) => [c.id, c]),
);

export function constellationMeta(id: string): ConstellationMeta | undefined {
  return CONSTELLATION_META_BY_ID.get(id);
}

/** All constellation ids that have drawn line segments. */
export function constellationIds(): string[] {
  return Object.keys(CONSTELLATION_LINES);
}

/** Line segments (catalog star index pairs) for a constellation. */
export function constellationSegments(id: string): readonly (readonly [number, number])[] {
  return CONSTELLATION_LINES[id] ?? [];
}

/** Virtual (un-snapped) line points, rare fallback. */
export function constellationFallbackPoints(id: string): readonly { ra: number; dec: number }[] {
  return CONSTELLATION_FALLBACK_POINTS[id] ?? [];
}

// ------------------------------------------------------------------- search
export interface SearchResult {
  objectId: string;
  name: string;
  type: string;
  /** 0 = exact prefix match, higher = fuzzier. */
  score: number;
}

let searchIndex: Map<string, string[]> | null = null;

function normalizeTerm(term: string): string {
  return term
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function addToIndex(terms: string[], id: string) {
  for (const raw of terms) {
    const term = normalizeTerm(raw);
    if (term.length < 2) continue;
    let bucket = searchIndex!.get(term);
    if (!bucket) {
      bucket = [];
      searchIndex!.set(term, bucket);
    }
    if (!bucket.includes(id)) bucket.push(id);
  }
}

function buildSearchIndex() {
  searchIndex = new Map();
  for (let i = 0; i < STAR_COUNT; i++) {
    const s = starCatalog.star(i);
    const id = `STAR:${i}`;
    const terms: string[] = [];
    if (s.name) terms.push(s.name);
    if (s.designation) terms.push(s.designation);
    if (s.hip) terms.push(`hip ${s.hip}`, `hip${s.hip}`);
    addToIndex(terms, id);
  }
  for (const dso of deepSkyObjects) {
    addToIndex([dso.id, dso.name], `DSO:${dso.index}`);
  }
  for (const c of CONSTELLATIONS) {
    addToIndex([c.id, c.name, c.english], `CONSTELLATION:${c.id}`);
  }
  for (const body of DYNAMIC_BODIES) {
    addToIndex([...bodySearchTerms(body)], bodyId(body));
  }
}

/**
 * Case-insensitive, accent-insensitive prefix search over names and
 * designations. Returns at most `limit` results, best matches first.
 */
export function searchSky(query: string, limit = 12): SearchResult[] {
  if (!searchIndex) buildSearchIndex();
  const q = normalizeTerm(query);
  if (q.length < 2) return [];
  const scored: { id: string; score: number }[] = [];
  // Exact prefix matches first, then contains matches.
  for (const [term, ids] of searchIndex!) {
    if (term.startsWith(q)) {
      for (const id of ids) scored.push({ id, score: 0 });
    } else if (term.includes(q)) {
      for (const id of ids) scored.push({ id, score: 1 });
    }
  }
  scored.sort((a, b) => a.score - b.score);
  const seen = new Set<string>();
  const out: SearchResult[] = [];
  for (const { id, score } of scored) {
    if (seen.has(id)) continue;
    seen.add(id);
    const meta = describeObject(id);
    if (!meta) continue;
    out.push({ objectId: id, name: meta.name, type: meta.type, score });
    if (out.length >= limit) break;
  }
  return out;
}

/** Resolve an object id to a CelestialObject (stars/dso/constellations). */
export function describeObject(id: string): CelestialObject | null {
  if (id.startsWith("STAR:")) {
    const index = parseInt(id.slice(5), 10);
    if (!Number.isInteger(index) || index < 0 || index >= STAR_COUNT) return null;
    const s = starCatalog.star(index);
    return {
      id,
      name: s.name || s.designation || `Star ${s.hip ?? index}`,
      type: "STAR",
      ra: s.raHours,
      dec: s.decDegrees,
      magnitude: s.magnitude,
      source: "catalog",
      constellation: s.constellation,
      aliases: [s.designation, s.hip ? `HIP ${s.hip}` : ""].filter(Boolean),
      provenance: "HYG v4.1",
    };
  }
  if (id.startsWith("DSO:")) {
    const dso = deepSkyObjects[parseInt(id.slice(4), 10)];
    if (!dso) return null;
    return {
      id,
      name: dso.name,
      type: deepSkyType(dso.type),
      ra: dso.ra / 15,
      dec: dso.dec,
      magnitude: dso.mag,
      source: "catalog",
      constellation: dso.con,
      sizeArcmin: dso.sizeArcmin,
      provenance: "Messier catalog",
    };
  }
  if (id.startsWith("CONSTELLATION:")) {
    const meta = CONSTELLATION_META_BY_ID.get(id.slice(14));
    if (!meta) return null;
    return {
      id,
      name: meta.name,
      type: "CONSTELLATION",
      ra: 0,
      dec: 0,
      magnitude: 99,
      source: "derived",
      aliases: [meta.id, meta.english],
      description: meta.english,
    };
  }
  return describeDynamicObject(id);
}

function deepSkyType(t: DeepSkyObject["type"]): CelestialObject["type"] {
  switch (t) {
    case "galaxy":
      return "GALAXY";
    case "nebula":
      return "NEBULA";
    case "planetary":
      return "NEBULA";
    case "cluster":
      return "CLUSTER";
    case "star":
      return "STAR";
  }
}
